import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Box,
  FormHelperText,
  CardMedia,
  Tooltip,
  Avatar,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import { useEffect, useState } from "react";
import ButtonComponent from "./ButtonComponent";
import ParagraphHighliter from "./paragraphHighliter";
import { useTranslation } from "react-i18next";

const SelectListUI = ({
  name,
  title,
  items,
  value,
  change,
  gridColumn,
  gridRow,
  error,
  touched,
  loading,
  multiple = true,
  buttonOnly = false,
}) => {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_MEDIA_URL;
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(value || (multiple ? [] : null));
  const [search, setSearch] = useState("");
  const handleOpen = () => setOpen(true);
  const filteredItems = search.trim()
    ? items.filter((item) =>
        (item.name || "").toLowerCase().includes(search.trim().toLowerCase())
      )
    : items;

  const isSelected = (id) =>
    multiple ? selected.includes(id) : selected === id;

  const handleItemClick = (id) => {
    let newSelected;
    if (multiple) {
      newSelected = selected.includes(id)
        ? selected.filter((v) => v !== id)
        : [...selected, id];
    } else {
      newSelected = selected === id ? null : id;
    }
    setSelected(newSelected);
    change({
      target: {
        name,
        value: newSelected,
      },
    });
  };

  const handleClear = () => {
    setSelected([]);
    change({
      target: {
        name,
        value: [],
      },
    });
  };

  useEffect(() => {
    setSelected(value || (multiple ? [] : null));
  }, [value, multiple]);
  const handleCancel = () => {
    setSelected(value || (multiple ? [] : null));
    setOpen(false);
  };

  const selectedItems = multiple
    ? items.filter((item) => selected && selected.includes(item._id))
    : items.filter((item) => selected && item._id === selected);

  const tooltipContent = selectedItems.length && (
    <Box>
      {selectedItems.map((item) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          key={item._id}
          sx={{ mb: 0.5 }}
        >
          <Avatar
            src={
              item.image
                ? `${apiUrl}/${item.image.replace(/\\/g, "/")}`
                : undefined
            }
            alt={item.name || ""}
            sx={{ width: 28, height: 28 }}
          />
          <Typography variant="body2">{item.name || ""}</Typography>
        </Stack>
      ))}
    </Box>
  );

  return (
    <Box sx={{ gridColumn, gridRow, width: buttonOnly ? "auto" : "100%", flexShrink: 0 }}>
      {buttonOnly ? (
        <Button
          variant="outlined"
          color="primary"
          size="medium"
          startIcon={<ChecklistRtlIcon />}
          onClick={handleOpen}
        >
          {title}
        </Button>
      ) : (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}
        >
          <Box>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              size="medium"
              startIcon={<ChecklistRtlIcon />}
              disabled={loading}
              onClick={handleOpen}
            >
              {title}
            </Button>
          </Box>
          <Tooltip
            title={tooltipContent}
            placement="top"
            arrow
            enterTouchDelay={0}
          >
            <Box
              sx={{
                cursor: selectedItems.length ? "pointer" : "default",
              }}
            >
              {selectedItems.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar
                    src={
                      selectedItems[0].image ||
                      selectedItems[0].icon ||
                      selectedItems[0].logo
                        ? `${apiUrl}/${(
                            selectedItems[0].image ||
                            selectedItems[0].icon ||
                            selectedItems[0].logo
                          ).replace(/\\/g, "/")}`
                        : undefined
                    }
                    alt={selectedItems[0].name || ""}
                    sx={{ width: 32, height: 32, border: "2px solid #1976d2" }}
                  />
                  {selectedItems[0].name && (
                    <Typography variant="body2" noWrap maxWidth={80}>
                      {selectedItems[0].name}
                    </Typography>
                  )}
                  {selectedItems.length > 1 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 0.5 }}
                    >
                      +{selectedItems.length - 1} autres
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>
          </Tooltip>
        </Box>
      )}
      {touched && error && (
        <FormHelperText sx={{ color: "error.main", mb: 2 }}>
          {error}
        </FormHelperText>
      )}
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle component="div">
          <Typography variant="h4" sx={{ mt: 2 }}>
            {title}
          </Typography>
          <input
            type="text"
            placeholder={t("search.byNameDots")}
            className="search-input"
            style={{
              paddingLeft: "10px",
              height: "37px",
              marginBottom: 16,
              borderRadius: 6,
              border: "1px solid #ccc",
              outline: "none",
            }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {filteredItems.length === 0 ? (
              <Grid item xs={12}>
                <Typography
                  align="center"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  Aucun résultat trouvé.
                </Typography>
              </Grid>
            ) : (
              filteredItems.map((item) => (
                <Grid item xs={6} sm={3} key={item._id}>
                  <Box
                    onClick={() => handleItemClick(item._id)}
                    sx={{
                      border: isSelected(item._id)
                        ? "2px solid #1976d2"
                        : "2px solid transparent",
                      borderRadius: 2,
                      p: 1,
                      cursor: "pointer",
                      position: "relative",
                      transition: "border 0.2s",
                      boxShadow: isSelected(item._id)
                        ? "0 0 8px 0 #1976d255"
                        : "none",
                    }}
                  >
                    <CardMedia
                      component="div"
                      sx={{
                        objectFit: "cover",
                        height: 0,
                        paddingTop: "56.25%",
                        backgroundImage: `url(${apiUrl}/${(
                          item.image ||
                          item.icon ||
                          item.logo
                        )?.replace(/\\/g, "/")})`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        backgroundSize: "contain",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        backgroundColor: "action.hover",
                      }}
                      image={`${apiUrl}/${(
                        item.image ||
                        item.icon ||
                        item.logo
                      )?.replace(/\\/g, "/")}`}
                    />
                    {item.name && (
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ mt: 0.5, mb: 0 }}
                      >
                        <ParagraphHighliter
                          title={item?.name}
                          search={search}
                        />
                      </Typography>
                    )}
                    {isSelected(item._id) && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          right: 8,
                          background: "#fff",
                          borderRadius: "50%",
                          boxShadow: 1,
                          p: "2px",
                          zIndex: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircleIcon
                          sx={{ fontSize: 24, color: "primary.main" }}
                        />
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <ButtonComponent
            text={t("fields.clearAll")}
            loading={false}
            onClick={handleClear}
            color="error"
          />
          <Button sx={{ mt: 3 }} onClick={handleCancel} color="secondary">
            {t("common.cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SelectListUI;
